import { useEffect, useState } from 'react';
import { useRecommendationsStore } from '../stores/recommendationsStore';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const StarIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const UsersIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const MetricCard = ({ title, value, subValue, icon: Icon }) => (
  <div className="bg-bg-secondary rounded-xl border border-border-color p-6">
    <div className="flex items-center justify-between mb-4">
      <div className="text-text-secondary">
        <Icon />
      </div>
    </div>
    <div className="text-3xl font-bold text-text-primary mb-1">{value}</div>
    <div className="text-sm text-text-secondary">{title}</div>
    {subValue && <div className="text-xs text-text-muted mt-2">{subValue}</div>}
  </div>
);

const PeriodSelector = ({ period, onChange }) => {
  const periods = [
    { value: '7days', label: '7 дней' },
    { value: '30days', label: '30 дней' },
    { value: '90days', label: '90 дней' },
  ];

  return (
    <div className="flex gap-2">
      {periods.map((p) => (
        <button
          key={p.value}
          onClick={() => onChange(p.value)}
          className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
            period === p.value
              ? 'bg-accent-primary text-white'
              : 'bg-bg-tertiary text-text-secondary hover:text-text-primary'
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
};

// Основной компонент страницы
export const Recommendations = () => {
  const {
    summary,
    isLoadingSummary,
    chartData,
    chartPeriod,
    isLoadingCharts,
    topUsers,
    isLoadingTopUsers,
    fetchSummary,
    fetchCharts,
    fetchTopUsers,
  } = useRecommendationsStore();

  const [page, setPage] = useState(0);
  const limit = 20;

  // Загрузка данных при монтировании
  useEffect(() => {
    fetchSummary();
    fetchCharts();
    fetchTopUsers(page + 1, limit);
  }, [page]);

  // Обработка смены периода
  const handlePeriodChange = (period) => {
    fetchCharts(period);
  };

  // Пагинация для "Порекомендовали"
  const totalPages = 10; // Примерно, реальное значение должно приходить с бэкенда

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setPage(newPage);
      fetchTopUsers(newPage + 1, limit);
    }
  };

  return (
    <div className="space-y-6">
      {/* Заголовок */}
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Аналитика рекомендаций</h1>
        <p className="text-sm text-text-secondary mt-1">
          Пользователи, нажавшие "Рекомендовать и получить +50 звезд"
        </p>
      </div>

      {/* Метрики */}
      {isLoadingSummary ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-bg-secondary rounded-xl border border-border-color p-6 animate-pulse">
              <div className="h-6 w-6 bg-bg-tertiary rounded mb-4" />
              <div className="h-8 w-20 bg-bg-tertiary rounded mb-2" />
              <div className="h-4 w-32 bg-bg-tertiary rounded" />
            </div>
          ))}
        </div>
      ) : summary ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MetricCard
            title="Всего рекомендовало"
            value={summary.total_recommended}
            subValue={`Сегодня: ${summary.today.count}`}
            icon={UsersIcon}
          />
          <MetricCard
            title="За 30 дней"
            value={`${summary.month.count}`}
            subValue={`Звезд: ${summary.month.stars} ⭐`}
            icon={StarIcon}
          />
          <MetricCard
            title="Выдано звезд"
            value={`${summary.total_stars} ⭐`}
            subValue={`Сегодня: ${summary.today.stars} ⭐`}
            icon={StarIcon}
          />
        </div>
      ) : null}

      {/* График рекомендаций - только количество пользователей */}
      <div className="bg-bg-secondary rounded-xl border border-border-color p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-text-primary">Динамика рекомендаций</h2>
          <PeriodSelector period={chartPeriod} onChange={handlePeriodChange} />
        </div>

        {isLoadingCharts ? (
          <div className="h-64 flex items-center justify-center">
            <div className="animate-spin h-8 w-8 border-2 border-accent-primary border-t-transparent rounded-full" />
          </div>
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis
                dataKey="date"
                stroke="rgba(255,255,255,0.5)"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return `${date.getDate()}.${String(date.getMonth() + 1).padStart(2, '0')}`;
                }}
              />
              <YAxis stroke="rgba(255,255,255,0.5)" tick={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(31, 41, 55, 0.95)',
                  border: '1px solid rgba(75, 85, 99, 0.5)',
                  borderRadius: '8px',
                  color: '#fff'
                }}
                labelFormatter={(value) => {
                  const date = new Date(value);
                  return date.toLocaleDateString('ru-RU');
                }}
                formatter={(value, name) => {
                  if (name === 'count') {
                    return [`${value} пользователей`, 'Рекомендовало'];
                  }
                  return [value, name];
                }}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#8b5cf6"
                strokeWidth={2}
                dot={{ fill: '#8b5cf6', r: 4 }}
                name="count"
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-64 flex items-center justify-center text-text-secondary">
            Нет данных для отображения
          </div>
        )}
      </div>

      {/* Порекомендовали - с пагинацией */}
      <div className="bg-bg-secondary rounded-xl border border-border-color p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-text-primary">Порекомендовали</h2>
        </div>

        {isLoadingTopUsers ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 bg-bg-tertiary rounded animate-pulse" />
            ))}
          </div>
        ) : topUsers.length > 0 ? (
          <>
            <div className="space-y-3">
              {topUsers.map((user, index) => (
                <div key={user.vk_user_id} className="flex items-center justify-between p-4 bg-bg-tertiary rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-8 h-8 bg-accent-primary/20 rounded-full text-accent-primary font-bold text-sm">
                      {page * limit + index + 1}
                    </div>
                    <div>
                      <div className="text-sm font-mono text-text-primary">{user.vk_user_id}</div>
                      <div className="text-xs text-text-muted">
                        Регистрация: {new Date(user.registered_at).toLocaleDateString('ru-RU')}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-text-primary">
                      <span className="font-semibold">{user.reward_amount} ⭐</span>
                    </div>
                    <div className="text-xs text-text-muted">
                      {new Date(user.rewarded_at).toLocaleDateString('ru-RU')}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Пагинация */}
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-text-secondary">
                Страница {page + 1}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 0}
                  className="px-4 py-2 bg-bg-tertiary border border-border-color rounded-lg text-text-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bg-primary transition-colors"
                >
                  Назад
                </button>
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={topUsers.length < limit}
                  className="px-4 py-2 bg-bg-tertiary border border-border-color rounded-lg text-text-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bg-primary transition-colors"
                >
                  Вперёд
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center text-text-secondary py-8">
            Нет данных
          </div>
        )}
      </div>
    </div>
  );
};
